// [PHASE B / #018 — B3-2]: Resolve a custom-inventory roll-table value
//   to the underlying item. The CustomRollTableDialog item picker emits
//   two value shapes for inventory references:
//
//     custom_inv_<invIdx>                      → inventory[invIdx]
//     custom_inv_<invIdx>_contents_<subIdx>    → inventory[invIdx].containedItems[subIdx]
//
//   The contained-items shape was added so authors can put items inside
//   a container (e.g. rope/torches inside a Dungeoneer's Pack) onto a
//   roll table directly. Pre-Phase B / #018 the picker only listed the
//   container itself; pre-this-helper the consumer in App.jsx parsed
//   the value with `parseInt(strip-prefix)` which would silently look
//   up the wrong index when given the new contained-item shape.
//
//   Per Rule 22, the parser lives here; both production (App.jsx
//   generateShopWithD100) and the unit test import this single source.

const TOP_LEVEL = /^custom_inv_(\d+)$/;
const CONTAINED = /^custom_inv_(\d+)_contents_(\d+)$/;

/**
 * Resolve a `custom_inv_*` value to the actual inventory item it refers to.
 *
 * @param {string} itemRef - The roll-table entry's `item` field — typically
 *   `'custom_inv_0'` or `'custom_inv_0_contents_2'`. Returns null for
 *   anything that doesn't match either shape.
 * @param {Array} inventory - The character's inventory array.
 * @returns {object|null} The referenced inventory item (or contained item),
 *   or null if the reference doesn't resolve.
 */
export function resolveCustomInvRef(itemRef, inventory) {
  if (typeof itemRef !== 'string' || !itemRef) return null;
  if (!Array.isArray(inventory)) return null;

  const containedMatch = itemRef.match(CONTAINED);
  if (containedMatch) {
    const invIdx = Number(containedMatch[1]);
    const subIdx = Number(containedMatch[2]);
    const parent = inventory[invIdx];
    if (!parent || !Array.isArray(parent.containedItems)) return null;
    return parent.containedItems[subIdx] || null;
  }

  const topMatch = itemRef.match(TOP_LEVEL);
  if (topMatch) {
    const invIdx = Number(topMatch[1]);
    return inventory[invIdx] || null;
  }

  return null;
}
